/*
  Footer: mobile navigation accordions and in-flow end-of-page panel motion.
*/

(() => {
  const DESKTOP_MENU_QUERY = '(min-width: 48rem)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const isDesktopNav = () => window.matchMedia(DESKTOP_MENU_QUERY).matches;
  const prefersReducedMotion = () => window.matchMedia(REDUCED_MOTION_QUERY).matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const syncNavDetails = (footer) => {
    const detailsList = footer.querySelectorAll('[data-footer-nav-details]');
    const desktop = isDesktopNav();

    detailsList.forEach((details) => {
      const summary = details.querySelector('summary');

      if (desktop) {
        details.open = true;
        if (summary) {
          summary.tabIndex = -1;
        }
        return;
      }

      if (summary) {
        summary.tabIndex = 0;
      }

      if (details.dataset.userToggled !== 'true') {
        details.open = false;
      }
    });
  };

  const bindNavDetails = (footer) => {
    if (footer.dataset.footerNavBound === 'true') {
      return;
    }

    footer.dataset.footerNavBound = 'true';

    footer.querySelectorAll('[data-footer-nav-details]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (isDesktopNav()) {
          details.open = true;
          return;
        }

        details.dataset.userToggled = 'true';
      });
    });

    const media = window.matchMedia(DESKTOP_MENU_QUERY);
    const onChange = () => syncNavDetails(footer);

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
    } else if (typeof media.addListener === 'function') {
      media.addListener(onChange);
    }

    syncNavDetails(footer);
  };

  const bindPanelMotion = (footer) => {
    const stage = footer.querySelector('[data-footer-stage]');
    const panel = footer.querySelector('[data-footer-panel]');

    if (!stage || !panel || footer.dataset.footerMotionBound === 'true') {
      return;
    }

    footer.dataset.footerMotionBound = 'true';

    let frame = 0;

    const panelTravel = () => {
      const styles = window.getComputedStyle(stage);
      const paddingTop = Number.parseFloat(styles.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0;

      return Math.max(0, stage.offsetHeight - paddingTop - paddingBottom - panel.offsetHeight);
    };

    const progress = () => {
      const rect = stage.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.top >= viewportHeight) {
        return 0;
      }

      return clamp((viewportHeight - rect.top) / Math.max(1, rect.height), 0, 1);
    };

    const apply = () => {
      frame = 0;

      if (!isDesktopNav() || prefersReducedMotion()) {
        panel.style.removeProperty('transform');
        return;
      }

      const offset = panelTravel() * progress();
      panel.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    };

    const requestApply = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(apply);
      }
    };

    window.addEventListener('scroll', requestApply, { passive: true });
    window.addEventListener('resize', requestApply, { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(requestApply);
      observer.observe(stage);
      observer.observe(panel);
    }

    [DESKTOP_MENU_QUERY, REDUCED_MOTION_QUERY].forEach((query) => {
      const media = window.matchMedia(query);
      if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', requestApply);
      } else if (typeof media.addListener === 'function') {
        media.addListener(requestApply);
      }
    });

    apply();
  };

  const initFooters = (container = document) => {
    container.querySelectorAll('[data-footer]:not([data-footer-initialized])').forEach((footer) => {
      footer.dataset.footerInitialized = 'true';
      bindNavDetails(footer);
      bindPanelMotion(footer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFooters(), { once: true });
  } else {
    initFooters();
  }

  document.addEventListener('shopify:section:load', (event) => initFooters(event.target));
})();
